import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';
import { X } from 'lucide-react';
import { createPortal } from 'react-dom';
import { useT } from '../i18n';
import { cx } from './ui';

type Tone = 'info' | 'ok' | 'error';
interface ToastItem {
  id: number;
  text: string;
  tone: Tone;
  action?: { label: string; onClick: () => void };
  sticky?: boolean;
}

interface ToastApi {
  show: (text: string, opts?: { tone?: Tone; action?: ToastItem['action']; sticky?: boolean; ms?: number }) => number;
  dismiss: (id: number) => void;
}

const Ctx = createContext<ToastApi | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const t = useT();
  const [items, setItems] = useState<ToastItem[]>([]);
  const next = useRef(1);
  const dismiss = useCallback((id: number) => setItems((xs) => xs.filter((x) => x.id !== id)), []);
  const show = useCallback<ToastApi['show']>(
    (text, opts) => {
      const id = next.current++;
      setItems((xs) => [...xs.slice(-3), { id, text, tone: opts?.tone ?? 'info', action: opts?.action, sticky: opts?.sticky }]);
      if (!opts?.sticky) setTimeout(() => dismiss(id), opts?.ms ?? 3500);
      return id;
    },
    [dismiss],
  );
  const api = useMemo(() => ({ show, dismiss }), [show, dismiss]);
  return (
    <Ctx.Provider value={api}>
      {children}
      {createPortal(
        <div
          className="pointer-events-none fixed inset-x-0 z-[60] flex flex-col items-center gap-2 px-4"
          style={{ bottom: 'calc(var(--safe-bottom) + 84px)' }}
          aria-live="polite"
        >
          {items.map((it) => (
            <div
              key={it.id}
              className={cx(
                'pointer-events-auto flex min-h-14 max-w-xl items-center gap-2 rounded-2xl py-2 pl-5 text-[16px] font-medium text-white shadow-lg',
                it.action || it.sticky ? 'pr-2' : 'pr-5',
                it.tone === 'error' ? 'bg-red-700' : it.tone === 'ok' ? 'bg-emerald-700' : 'bg-navy-800',
              )}
              style={{ animation: 'kgr-toast-in 160ms ease-out' }}
            >
              <span className="mr-1">{it.text}</span>
              {it.action ? (
                <button
                  type="button"
                  className="min-h-11 shrink-0 rounded-xl bg-white/15 px-4 font-semibold hover:bg-white/25"
                  onClick={() => {
                    it.action?.onClick();
                    dismiss(it.id);
                  }}
                >
                  {it.action.label}
                </button>
              ) : null}
              {it.sticky ? (
                <button
                  type="button"
                  aria-label={t('common.close')}
                  title={t('common.close')}
                  className="inline-flex size-11 shrink-0 items-center justify-center rounded-xl text-white/85 hover:bg-white/15"
                  onClick={() => dismiss(it.id)}
                >
                  <X className="size-5" aria-hidden />
                </button>
              ) : null}
            </div>
          ))}
        </div>,
        document.body,
      )}
    </Ctx.Provider>
  );
}

export function useToast(): ToastApi {
  const c = useContext(Ctx);
  if (!c) throw new Error('ToastProvider missing');
  return c;
}
